import * as QueryString from 'qs';
import * as React from 'react';
import Moment from 'react-moment';
import { RouteComponentProps } from 'react-router';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/internal/operators';
import pjson from '../../../package.json';
import BusStopService from '../../api/stadtbus/bus_stop_service';
import PingService from '../../api/stadtbus/ping_service';
import PredictionsService from '../../api/stadtbus/predictions_service';
import { IBusStop } from '../../interfaces/stadtbus/bus_stop'
import { IPrediction } from '../../interfaces/stadtbus/predictions'
import { lazyInject } from '../../inversify';
import Theme from './css/bustheme';
import './css/monitor.css';
import { LemgoDigitalLogo, StadtbusLogo } from './stadtbus_logo';

interface IProps {
  id: string,
}

interface IHaltestelleComponentProps extends Partial<RouteComponentProps<IProps>> {
}

interface IHaltestelleComponentState {
  busStop: IBusStop | undefined,
  predictions: IPrediction[],
  groupedPredictions: IPredictionsViewModel[],
  hasData: boolean,
  isRealTime: boolean,
  showStopName: boolean,
  groupLines: boolean,
}

interface IPredictionsViewModel {
  line: string,
  predictions: IPrediction[],
}

class HaltestelleComponent extends React.Component<IHaltestelleComponentProps, IHaltestelleComponentState> {

  @lazyInject(PredictionsService)
  private readonly predictionsService: PredictionsService;
  @lazyInject(BusStopService)
  private readonly busStopService: BusStopService;
  @lazyInject(Theme)
  private readonly theme: Theme;
  @lazyInject(PingService)
  private readonly pingService: PingService;

  // emits an event if the component is unmounted
  private readonly onUnmount: Subject<boolean> = new Subject<boolean>();

  constructor(props: IHaltestelleComponentProps) {
    super(props);

    const query = this.props.location && QueryString.parse(this.props.location.search, {ignoreQueryPrefix: true});

    this.state = {
      busStop: undefined,
      groupLines: !(query && query.group === 'false'),
      groupedPredictions: [],
      hasData: false,
      isRealTime: true,
      predictions: [],
      showStopName: query && query.stopname === 'true' || false,
    };
  }

  public render() {
    if (this.props.location) {
      const query = QueryString.parse(this.props.location.search, {ignoreQueryPrefix: true});
      if (query.v === '2') {
        return this.renderV2();
      } else if (query.v === '3') {
        return this.renderV2(true, true)
      }
    }

    // ---------------------- Bus Stop ----------------------

    const stopStyle = {
      marginBottom: '0.615em', // 32px / 52px
      marginLeft: '0px',
      marginRight: '0px',
      marginTop: '0px',
      paddingBottom: '1.23em', // 32px / 52px
      paddingTop: '1.23em', // 32px / 52px
      textAlign: 'center' as 'center',
      width: "100%"
    };

    const centerAlign = {
      textAlign: 'center' as 'center'
    };

    const stop = (
      <div className='container headlineGradient' style={stopStyle}>
        <div className='col-md-12 space-bottom'>
          <StadtbusLogo width={'10.77em'} style={{display: 'block', margin: 'auto'}}/>
        </div>
        <div className='col-md-12 space-bottom'>
          Haltestelle: {this.state.busStop ? this.state.busStop.name : this.props.match!.params.id}
        </div>
        <div className='col-md-12' style={centerAlign}>
          <Moment format={'DD.MM.YYYY'}/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <Moment interval={250} format={'HH:mm:ss'}/>
        </div>
      </div>
    );

    // ---------------------- Table ----------------------

    const spacerRow = <tr className={'spacer'}>
      <td colSpan={4}/>
    </tr>;

    const table = (
      <table className="haltestelle container">
        <thead>
        <tr className="lineBlack">
          <th className="col-md-2">Linie</th>
          <th className="col-md-6">Richtung</th>
          <th className="col-md-4" colSpan={2}>Abfahrtszeit</th>
        </tr>
        </thead>
        <tbody>
        {spacerRow}
        {this.state.groupedPredictions.map((row: IPredictionsViewModel, rowIdx, rows) =>
          row.predictions.map((p: IPrediction, idx, predictions) => {
            const columns = [];

            if (idx === 0) {
              columns.push(
                <td className={'line-label col-md-2 linetext'}
                    key={'line_header'}
                    rowSpan={predictions.length}>
                  {row.line}
                </td>
              );
            }

            columns.push(
              <td className={'col-md-6 linetext'}
                  key={'destination'}>
                {p.lineDestination}
              </td>
            );

            columns.push(
              <td className={'col-md-2 linetext'}
                  colSpan={p.isRealTime ? 1 : 2} key={'time'}>
                <Moment format='HH:mm' date={p.shouldTime}/>
              </td>
            );

            if (p.isRealTime) {
              columns.push(
                <td className={'col-md-2 linetext'}
                    key={'is_realtime'}>
                  {p.delay < 0 ? '' : '+'}{p.delay}
                </td>
              );
            }

            const generatedRows = [(
              <tr
                className={this.theme.getLineColor(row.line, true)}
                key={`${p.tripID}.${p.journeyRef}`}>
                {columns}
              </tr>
            )];

            if (idx === row.predictions.length - 1 && rowIdx !== rows.length - 1) {
              generatedRows.push(spacerRow);
            }

            return generatedRows;
          }))}
        </tbody>
      </table>
    );

    // ---------------------- Main ----------------------

    const mainStyle = {
      color: 'white',
      fontFamily: 'verdana',
      // 52px / 16px
      fontSize: '3.25em',
    };

    let lauftext = <span>Auch als Webanwendung: stadtbus.lemgo-digital.de</span>;

    if (!this.state.isRealTime) {
      lauftext =
        <span>Leider liegen aktuell keine Echtzeitprognosen vor &mdash; Auch als Webanwendung: stadtbus.lemgo-digital.de</span>;
    }

    return (
      <div className="root" style={{width: "100%", height: "100%"}}>
        {this.state.groupedPredictions.length > 0 ? (
          <div style={mainStyle}>
            {stop}
            {table}
            <LemgoDigitalLogo style={{display: 'block', margin: '32px auto 4px', width: '50%'}}/>
            <p style={{color: "gray", textAlign: "center", marginTop: "4px"}}>
              Pilotversion {pjson.version}
            </p>
            <p className="redIndicator marquee" style={{marginTop: "16px"}}>
              {lauftext}
            </p>
          </div>
        ) : (
          <div style={{width: "1080px", height: "1920px", backgroundColor: "black"}}/>
        )}
      </div>
    );
  }

  public renderV2(oldSpacer: boolean = false, fullLineLabel: boolean = false) {
    const spacerRow = <tr className={oldSpacer ? 'spacer' : 'spacer-v2'}>
      <td colSpan={4}/>
    </tr>;

    const stop = this.state.showStopName && (
      <div className="lineBlack text-center">
        {this.state.busStop && this.state.busStop!.name || this.props.match!.params.id}
      </div>
    );

    const renderPredictions = (predictions: IPrediction[], row?: IPredictionsViewModel, rowIdx?: number, rows?: IPredictionsViewModel[]) => {
      
      if (!this.state.hasData) {
        return <tr className="lineGray">
          <td
            className="linetext text-center"
            colSpan={4}>
            Leider liegen aktuell keine Fahrplaninformationen vor
          </td>
        </tr>
      }
      
      if (!row && predictions.length === 0) {
        const generatedRows = [];
        generatedRows.push(
        <tr className="lineGray">
          <td
            className="linetext text-center"
            colSpan={4}>
            Derzeit kein Linienbetrieb
          </td>
        </tr>
        );
        generatedRows.push(spacerRow);
        generatedRows.push(
          <tr className="lineGray">
            <td
              className="linetext text-left"
              colSpan={4}>
                <table style={{marginLeft: "15px", marginRight: "15px"}}>
                  <tr>
                    <td colSpan={2}>Bleiben Sie mobil mit dem Anruf-Sammel-Taxi!</td>
                  </tr>
                  <tr style={{fontSize: "40px"}}>
                    <td colSpan={2}>Anmeldung 60 Minuten vor der gewünschten Abfahrt unter 05261/6673950.</td>
                  </tr>
                  <tr style={{fontSize: "40px"}}>
                    <td>Montag bis Freitag:</td>
                    <td>19:45–00:15 Uhr</td>
                  </tr>
                  <tr style={{fontSize: "40px"}}>
                    <td>Samstag:</td>
                    <td>16:45–00:15 Uhr</td>
                  </tr>
                  <tr style={{fontSize: "40px"}}>
                    <td>Sonn- und Feiertage:</td>
                    <td>08:45–22:15 Uhr</td>
                  </tr>
                </table>
            </td>
          </tr>
          );
      return generatedRows;
      }

      return predictions
        .map((p: IPrediction, idx: number) => {
          const columns = [];

          const lineName = row && row.line || p.lineName;
          const lineLabel = fullLineLabel ? lineName : lineName.replace(/^88/, '');

          if (row === undefined) {
            columns.push(
              <td className={'line-label col-md-2 linetext text-center'} key={'line_header'}>
                {lineLabel}
              </td>
            );
          } else if (idx === 0) {
            columns.push(
              <td className={'line-label col-md-2 linetext text-center'}
                  key={'line_header'}
                  rowSpan={predictions.length}>
                {lineLabel}
              </td>
            );
          }

          columns.push(
            <td className={'col-md-6 linetext'}
                key={'destination'}>
              {p.lineDestination}
            </td>
          );

          columns.push(
            <td className={'col-md-2 linetext'}
                colSpan={p.isRealTime ? 1 : 2} key={'time'}>
              <Moment format='HH:mm' date={p.shouldTime}/>
            </td>
          );

          if (p.isRealTime) {
            columns.push(
              <td className={'col-md-2 linetext'}
                  key={'is_realtime'}>
                {p.delay < 0 ? '' : '+'}{p.delay}
              </td>
            );
          }

          const generatedRows = [(
            <tr
              className={this.theme.getLineColor(lineName)}
              key={`${p.tripID}.${p.journeyRef}`}>
              {columns}
            </tr>
          )];

          if (row && rows && idx === predictions.length - 1 && rowIdx !== rows.length - 1
            || !row && idx !== predictions.length - 1) {
            generatedRows.push(spacerRow);
          }

          return generatedRows;
        })
    };

    const predictionRows = (this.state.groupLines && this.state.predictions.length > 0) ?
      this.state.groupedPredictions.map((row: IPredictionsViewModel, rowIdx, rows) =>
        renderPredictions(row.predictions, row, rowIdx, rows))
      : renderPredictions(this.state.predictions);

    const table = (
      <table className="haltestelle container">
        <thead>
        <tr className="lineBlack">
          <th className="col-md-2">Linie</th>
          <th className="col-md-6">Richtung</th>
          <th className="col-md-4" colSpan={2}>Abfahrtszeit</th>
        </tr>
        </thead>
        <tbody>
        {spacerRow}
        {predictionRows}
        </tbody>
      </table>
    );

    return (
      <div className="root" style={{height: "100%", width: "100%"}}>
        <div style={{
          color: 'white',
          fontSize: '4em', // 64px / 16px
          fontWeight: 'bold',
        }}>
          {stop}
          {table}

          {/* <p style={{color: "gray", textAlign: "center", marginTop: "4px"}}>
            Pilotversion {pjson.version}
          </p> */}
        </div>
      </div>
    );
  }

  public componentWillMount() {
    this.busStopService.onBusStopExists(this.props.match!.params.id)
      .pipe(
        takeUntil(this.onUnmount),
        tap(stop => {
          document.title = "Haltestelle " + (stop ? stop.name : this.props.match!.params.id);
        }),
      ).subscribe(busStop => {
      this.setState({busStop});
    });

    this.predictionsService.onStopPredictions(this.props.match!.params.id, false)
      .pipe(takeUntil(this.onUnmount))
      .subscribe(predictions => {
        const dataReceived = predictions.length > 0;
        // Reformat [] -> [][] (Same line in same array)
        const viewModel: IPredictionsViewModel[] = [];

        // filter to get today's data only
        const today = new Date();
        predictions = predictions.filter(p =>
          p.shouldTime.getDate() === today.getDate()
          && p.shouldTime.getMonth() === today.getMonth()
          && p.shouldTime.getFullYear() === today.getFullYear());

        this.predictionsService.filterNextDeparturePerLineInstant(predictions)
          .forEach((prediction) => {
            let lineModel = viewModel.find(m => m.line === prediction.lineName);

            if (!lineModel) {
              viewModel.push(lineModel = {
                line: prediction.lineName,
                predictions: []
              });
            }

            lineModel.predictions.push(prediction);
          });

        this.setState({
          groupedPredictions: viewModel,
          hasData: dataReceived,
          isRealTime: predictions.find(p => p.isRealTime) !== undefined,
          predictions,
        });
      });
  }

  public componentDidMount() {
    if (this.props.location) {
      const query = QueryString.parse(this.props.location.search, {ignoreQueryPrefix: true});

      if (query.ping_topic) {
        this.pingService
          .ping(`haltestelle_${query.ping_topic}`, () => ({
            predictionCount: this.state.groupedPredictions.length,
          }))
          .pipe(takeUntil(this.onUnmount))
          .subscribe();
      }
    }
  }

  public componentWillUnmount() {
    this.onUnmount.next(true);
  }
}

export default HaltestelleComponent;



// WEBPACK FOOTER //
// ./src/components/stadtbus/haltestelle_component.tsx